import type { TemplatePattern } from '../types';
import { templateCategories } from '../data/templates';
import type { BeadPalette } from '../types';
import MiniPatternCanvas from './MiniPatternCanvas';
import { totalBeads, usedColorCount } from '../lib/pattern';
import { difficultyLabel, t, templateCategoryLabel, templateTitle, type Locale } from '../i18n';

type TemplateGalleryProps = {
  templates: TemplatePattern[];
  palette: BeadPalette;
  locale: Locale;
  activeCategory: TemplatePattern['category'] | 'all';
  onCategoryChange: (category: TemplatePattern['category'] | 'all') => void;
  onSelectTemplate: (template: TemplatePattern) => void;
};

export default function TemplateGallery({
  templates,
  palette,
  locale,
  activeCategory,
  onCategoryChange,
  onSelectTemplate
}: TemplateGalleryProps) {
  return (
    <section className="template-gallery" aria-label={t(locale, 'starterTemplates')}>
      <div className="section-heading-row">
        <div>
          <h2>{t(locale, 'browseTemplates')}</h2>
          <p>{t(locale, 'starterPatternCount', { count: templates.length })}</p>
        </div>
      </div>

      <div className="category-tabs" role="tablist" aria-label={t(locale, 'templateCategories')}>
        {templateCategories.map((category) => (
          <button
            key={category.id}
            className={activeCategory === category.id ? 'active' : ''}
            type="button"
            onClick={() => onCategoryChange(category.id)}
          >
            {templateCategoryLabel(locale, category.id)}
          </button>
        ))}
      </div>

      <div className="template-grid">
        {templates.map((template) => {
          const title = templateTitle(locale, template);
          return (
            <button
              className="template-card"
              type="button"
              key={template.id}
              onClick={() => onSelectTemplate(template)}
            >
              <MiniPatternCanvas
                width={template.width}
                height={template.height}
                cells={template.cells}
                palette={palette}
                label={t(locale, 'templatePreview', { title })}
              />
              <span className="template-title">{title}</span>
              <span className="template-meta">
                {t(locale, 'patternMeta', {
                  width: template.width,
                  height: template.height,
                  colors: usedColorCount(template.counts),
                  beads: totalBeads(template.counts)
                })}
              </span>
              <span className={`difficulty difficulty-${template.difficulty}`}>
                {difficultyLabel(locale, template.difficulty)}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
